/**
 * Tempo de Produção por Peça — agrega peças dos cards TC do lote,
 * unifica iguais (desc normalizada + medida + material) e calcula datas.
 */

import { numerosLoteDeTexto } from '../components/corte/familiasProdutos';
import { normalizarNomeProdutoPalete } from './casarProdutoPaleteLotes';
import { normalizarChaveProduto } from './resolverProdutoPontuacao';
import { cardTempoCorteFoiExcluidoManual } from './cardsTempoCorteExcluidos';

export interface PecaTempoCorteBruta {
  descricao: string;
  qtd: number;
  medida: string;
  material: string;
  metrosQuadrados: number;
  produtoOrigem?: string;
  cardNome?: string;
}

export interface LinhaTempoProducaoPeca {
  id: string;
  descricaoExibicao: string;
  chaveUnificacao: string;
  qtd: number;
  medida: string;
  material: string;
  metrosQuadrados: number;
  /** Tempo total da linha (manual), em minutos. */
  tempoLinhaMinutos: number;
  origens: string[];
}

export interface RegistroTempoProducaoPecaLote {
  loteChave: string;
  loteDigitado: string;
  linhas: LinhaTempoProducaoPeca[];
  tempoPercaMinutos: number;
  dataHoraInicioPlanejado?: string;
  dataHoraTerminoProducaoCalculado?: string;
  atualizadoEm: string;
  /** Só nesta aba: impede editar tempos/perca; sync do Tempo de Corte continua. */
  edicaoBloqueada?: boolean;
}

const CORES_NOME_PECA = [
  'OFF WHITE/CEDRO/RIPADO 3D',
  'OFF WHITE/CEDRO/RIPADO',
  'OFF WHITE/NATURALE NEW',
  'OFF WHITE/CEDRO',
  'PRETO/NATURALE NEW',
  'FENDI/FREIJO NOBRE',
  'FREIJO NOBRE/FENDI',
  'OFF/CEDRO/RIPADO',
  'CEDRO/RIPADO 3D',
  'CEDRO/OFF WHITE',
  'BRANCO/CEDRO',
  'FREIJO/FENDI',
  'FENDI/FREIJO',
  'PRETO/NATURALE',
  'PRETO/CEDRO',
  'CEDRO/PRETO',
  'OFF/NATURALE',
  'OFF/CEDRO',
  'CEDRO/OFF',
  'NATURALE NEW',
  'OFF WHITE',
  'RIPADO 3D',
  'NATURALE',
  'FREIJO NOBRE',
  'FREIJO',
  'FENDI',
  'BRANCA',
  'BRANCO',
  'CEDRO',
  'PRETO',
  'OFF',
].sort((a, b) => b.length - a.length);

const ABREVIACOES_PECA: Array<[RegExp, string]> = [
  [/\bREG\s+TRAS\b/g, 'REGUA TRASEIRA'],
  [/\bREGUA\s+TRAS\b/g, 'REGUA TRASEIRA'],
  [/\bPAINEL\s+SUP\b/g, 'PAINEL SUPERIOR'],
  [/\bPAINEL\s+INF\b/g, 'PAINEL INFERIOR'],
  [/\bFRENTE\s+GAV\b/g, 'FRENTE DE GAVETA'],
  [/\bLAT\s+GAV\b/g, 'LATERAL DE GAVETA'],
  [/\bMODL\b/g, 'MOLDURA'],
  [/\bDIV\b/g, 'DIVISORIA'],
  [/\bREG\b/g, 'REGUA'],
  [/\bLAT\b/g, 'LATERAL'],
];

function normalizarTextoPeca(s: string): string {
  return normalizarChaveProduto(s).replace(/\s+/g, ' ').trim();
}

function normalizarMedidaPeca(medida: string): string {
  return String(medida || '')
    .toUpperCase()
    .replace(/\s+/g, '')
    .replace(/,/g, '.')
    .trim();
}

function normalizarMaterialPeca(material: string): string {
  return normalizarChaveProduto(material).replace(/\s+/g, ' ').trim();
}

function escaparRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Remove tokens de cor do nome da peça (ex.: LATERAL ESQ CEDRO → LATERAL ESQ). */
export function limparCoresNomePeca(descricao: string): string {
  let s = normalizarTextoPeca(descricao);

  // Cores costumam vir no final do nome — remove do fim para dentro (mais longas primeiro).
  let removeuNoFim = true;
  while (removeuNoFim) {
    removeuNoFim = false;
    for (const cor of CORES_NOME_PECA) {
      const tok = normalizarTextoPeca(cor);
      if (!tok) continue;
      const reFinal = new RegExp(`(?:^|\\s)${escaparRegex(tok)}$`);
      if (reFinal.test(s)) {
        s = s.replace(reFinal, '').trim();
        removeuNoFim = true;
        break;
      }
    }
  }

  for (const cor of CORES_NOME_PECA) {
    const tok = normalizarTextoPeca(cor);
    if (!tok) continue;
    const re = new RegExp(`\\b${escaparRegex(tok)}\\b`, 'g');
    s = s.replace(re, ' ');
  }
  return s.replace(/\s+/g, ' ').trim();
}

const RE_ESQ = /\bESQ(?:UERDA)?\b/;
const RE_DIR = /\bDIR(?:EITA)?\b/;
const RE_DIVISORIA = /\bDIVISORIA\b/;
const RE_CENTRAL = /\bCENTRAL\b/;

function extrairBasePosicional(descricao: string): string {
  let s = normalizarTextoPeca(expandirAbreviacoesNomePeca(descricao));
  s = s.replace(RE_ESQ, ' ').replace(RE_DIR, ' ').replace(RE_CENTRAL, ' ').replace(/\s+/g, ' ').trim();
  return s;
}

function classificarPecaKit(descricao: string): 'esq' | 'dir' | 'divisoria' | null {
  const s = normalizarTextoPeca(descricao);
  const temEsq = RE_ESQ.test(s);
  const temDir = RE_DIR.test(s);
  const temDiv = RE_DIVISORIA.test(s);
  if (temDiv && !temEsq && !temDir) return 'divisoria';
  if (temEsq && !temDir) return 'esq';
  if (temDir && !temEsq) return 'dir';
  return null;
}

/** Kit estrutural (lateral/divisória do móvel) — não inclui frente nem lateral de gaveta. */
function ehPecaKitLateralEstrutural(descricao: string): boolean {
  const s = normalizarTextoPeca(expandirAbreviacoesNomePeca(descricao));
  if (/\bFRENTE\s+DE\s+GAVETA\b/.test(s)) return false;
  if (/\bLATERAL\s+DE\s+GAVETA\b/.test(s)) return false;
  if (RE_DIVISORIA.test(s) && !RE_ESQ.test(s) && !RE_DIR.test(s)) return true;
  return /\bLATERAL\b/.test(s) && (RE_ESQ.test(s) || RE_DIR.test(s));
}

/** Rótulo do kit lateral estrutural (LATERAL ESQ/DIR + DIVISORIA). */
function descricaoKitLateral(nomes: string[]): string | null {
  if (!nomes.every((n) => ehPecaKitLateralEstrutural(n))) return null;

  let esq = false;
  let dir = false;
  let div = false;
  for (const n of nomes) {
    const t = classificarPecaKit(n);
    if (t === 'esq') esq = true;
    if (t === 'dir') dir = true;
    if (t === 'divisoria') div = true;
  }
  if (esq && dir && div) return 'LATERAL ESQ, DIR E DIVISORIA';
  if (esq && dir) return 'LATERAL ESQ E DIR';
  if (esq && div) return 'LATERAL ESQ E DIVISORIA';
  if (dir && div) return 'LATERAL DIR E DIVISORIA';
  return null;
}

/** Trio CENTRAL + ESQ + DIR com mesmo nome-base (ex.: frente de gaveta). */
function rotuloCentralEsquerdaDireita(nomes: string[]): string | null {
  if (nomes.length !== 3) return null;

  const bases = nomes.map(extrairBasePosicional);
  const baseUnica = [...new Set(bases.filter(Boolean))];
  if (baseUnica.length !== 1) return null;

  const temCentral = nomes.some((n) => RE_CENTRAL.test(normalizarTextoPeca(n)));
  const temEsq = nomes.some((n) => RE_ESQ.test(normalizarTextoPeca(n)));
  const temDir = nomes.some((n) => RE_DIR.test(normalizarTextoPeca(n)));
  if (!temCentral || !temEsq || !temDir) return null;

  return `${baseUnica[0]} CENTRAL, ESQ E DIR`;
}

/** Par ESQ+DIR com mesmo nome-base (ex.: FRENTE DE GAVETA ESQ + DIR). */
function rotuloParEsquerdaDireita(nomes: string[]): string | null {
  if (nomes.length < 2) return null;

  const bases = nomes.map(extrairBasePosicional);
  const baseUnica = [...new Set(bases.filter(Boolean))];
  if (baseUnica.length !== 1) return null;

  const temEsq = nomes.some((n) => RE_ESQ.test(normalizarTextoPeca(n)));
  const temDir = nomes.some((n) => RE_DIR.test(normalizarTextoPeca(n)));
  if (!temEsq || !temDir) return null;

  return `${baseUnica[0]} ESQ E DIR`;
}

/** Junta nomes distintos: "A, B E C" (ou rótulo do kit lateral quando couber). */
export function juntarNomesPecas(nomes: string[]): string {
  const unicos = [...new Set(nomes.map((n) => n.trim()).filter(Boolean))];
  if (unicos.length === 0) return '';
  if (unicos.length === 1) return unicos[0];

  const kit = descricaoKitLateral(unicos);
  if (kit) return kit;

  const trio = rotuloCentralEsquerdaDireita(unicos);
  if (trio) return trio;

  const par = rotuloParEsquerdaDireita(unicos);
  if (par) return par;

  unicos.sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }));
  if (unicos.length === 2) return `${unicos[0]} E ${unicos[1]}`;
  const ultimo = unicos[unicos.length - 1];
  return `${unicos.slice(0, -1).join(', ')} E ${ultimo}`;
}

function chaveMedidaMaterial(medida: string, material: string): string {
  return `${normalizarMedidaPeca(medida)}|${normalizarMaterialPeca(material)}`;
}

const ORDEM_ESP_AGLO = [12, 15, 25] as const;
const ORDEM_ESP_MDF = [12, 15, 25, 30] as const;

function extrairEspessuraMaterial(material: string): number {
  const nums = normalizarMaterialPeca(material).match(/\d+/g);
  if (!nums?.length) return 0;
  return parseInt(nums[nums.length - 1], 10) || 0;
}

function tipoMaterialBasico(material: string): 'aglo' | 'mdf' | 'outro' {
  const n = normalizarMaterialPeca(material);
  if (/\bAGLO/.test(n)) return 'aglo';
  if (/\bMDF/.test(n)) return 'mdf';
  return 'outro';
}

/** AGLO 12→15→25, MDF 12→15→25→30; espessuras 3 e 5 no final. */
export function indiceOrdemMaterialProducaoPeca(material: string): number {
  const esp = extrairEspessuraMaterial(material);
  if (esp === 3) return 9000;
  if (esp === 5) return 9001;

  const tipo = tipoMaterialBasico(material);
  if (tipo === 'aglo') {
    const i = (ORDEM_ESP_AGLO as readonly number[]).indexOf(esp);
    return i >= 0 ? i : 100 + esp;
  }
  if (tipo === 'mdf') {
    const base = ORDEM_ESP_AGLO.length;
    const i = (ORDEM_ESP_MDF as readonly number[]).indexOf(esp);
    return i >= 0 ? base + i : base + 100 + esp;
  }
  return 5000 + esp;
}

/** Primeira dimensão = largura; segunda = comprimento/altura. */
export function parseMedidaDimensoesPeca(medida: string): { largura: number; altura: number } {
  const m = String(medida || '').match(/(\d+(?:[.,]\d+)?)\s*[xX×]\s*(\d+(?:[.,]\d+)?)/);
  if (!m) return { largura: 0, altura: 0 };
  return {
    largura: Number(m[1].replace(',', '.')) || 0,
    altura: Number(m[2].replace(',', '.')) || 0,
  };
}

/** Material (AGLO→MDF) e, dentro do material, largura crescente. */
export function compararLinhasTempoProducaoPeca(
  a: Pick<LinhaTempoProducaoPeca, 'material' | 'medida' | 'descricaoExibicao'>,
  b: Pick<LinhaTempoProducaoPeca, 'material' | 'medida' | 'descricaoExibicao'>
): number {
  const ordMat = indiceOrdemMaterialProducaoPeca(a.material) - indiceOrdemMaterialProducaoPeca(b.material);
  if (ordMat !== 0) return ordMat;

  const ma = parseMedidaDimensoesPeca(a.medida);
  const mb = parseMedidaDimensoesPeca(b.medida);
  if (ma.largura !== mb.largura) return ma.largura - mb.largura;
  if (ma.altura !== mb.altura) return ma.altura - mb.altura;

  return a.descricaoExibicao.localeCompare(b.descricaoExibicao, 'pt-BR', { sensitivity: 'base' });
}

export function expandirAbreviacoesNomePeca(descricao: string): string {
  let s = limparCoresNomePeca(descricao);
  for (const [re, para] of ABREVIACOES_PECA) {
    s = s.replace(re, para);
  }
  return s.replace(/\s+/g, ' ').trim();
}

export function chaveUnificacaoPeca(descricao: string, medida: string, material: string): string {
  return `${expandirAbreviacoesNomePeca(descricao)}|${normalizarMedidaPeca(medida)}|${normalizarMaterialPeca(material)}`;
}

export function descricaoExibicaoPeca(descricao: string): string {
  return expandirAbreviacoesNomePeca(descricao);
}

export function loteCompartilhaNumero(loteCard: string, loteDigitado: string): boolean {
  const dig = String(loteDigitado || '').trim();
  const card = String(loteCard || '').trim();
  if (!dig || !card) return false;
  const numsDig = numerosLoteDeTexto(dig);
  if (numsDig.length === 0) {
    return normalizarChaveProduto(card) === normalizarChaveProduto(dig);
  }
  const numsCard = numerosLoteDeTexto(card);
  if (numsCard.length === 0) {
    return normalizarChaveProduto(card) === normalizarChaveProduto(dig);
  }
  return numsDig.some((n) => numsCard.includes(n));
}

/** Mesma regra da lista filtrada do Tempo de Corte (campo lote do card).
 *  Usa só o número do lote no card — não puxa fantasmas por substring inversa
 *  (ex.: lote "86" casando com "8670") nem por lotes embutidos em originais
 *  de unificados de outros lotes. */
export function cardTempoCortePertenceAoLote(
  card: {
    lote?: string;
    produtosOrigemCompleto?: Array<{ lote?: string }>;
    produtosOrigem?: Array<{ lote?: string }>;
  },
  loteDigitado: string
): boolean {
  const loteFiltro = String(loteDigitado || '').trim();
  if (!loteFiltro) return false;
  const numsFiltro = numerosLoteDeTexto(loteFiltro);
  const numsCard = numerosLoteDeTexto(String(card.lote || ''));

  if (numsFiltro.length > 0) {
    if (numsCard.length > 0) {
      return numsFiltro.some((n) => numsCard.includes(n));
    }
    return normalizarChaveProduto(String(card.lote || '')) === normalizarChaveProduto(loteFiltro);
  }

  const pl = String(card.lote || '').toUpperCase();
  const loteUpper = loteFiltro.toUpperCase();
  return pl === loteUpper || pl.includes(loteUpper);
}

/** Cards reais do lote, sem excluídos manuais.
 *  Mantém produto e MDF/AGLO como cards distintos (não remove “MDF” do nome).
 *  Só evita o mesmo card duas vezes (mesmo id, ou mesmo lote+nome completo). */
export function listarCardsTempoCorteDoLote<
  T extends {
    id?: string;
    nome?: string;
    lote?: string;
    itens?: unknown[];
    dataCriacaoISO?: string;
    salvoEm?: string;
  },
>(produtosTc: ReadonlyArray<T>, loteDigitado: string, foiExcluido?: (card: T) => boolean): T[] {
  const filtrados = produtosTc.filter((c) => {
    if (!cardTempoCortePertenceAoLote(c, loteDigitado)) return false;
    if (foiExcluido?.(c)) return false;
    return true;
  });

  const porChave = new Map<string, T>();
  for (const card of filtrados) {
    const id = String(card.id || '').trim();
    const nums = numerosLoteDeTexto(String(card.lote || '')).join('-');
    // Nome completo (com MDF/AGLO) — “X” e “X MDF” são cards diferentes.
    const nomeKey = normalizarChaveProduto(String(card.nome || ''));
    const chave =
      id ||
      `${nums || normalizarChaveProduto(String(card.lote || ''))}|${nomeKey || 'sem-nome'}`;
    const prev = porChave.get(chave);
    if (!prev) {
      porChave.set(chave, card);
      continue;
    }
    // Mesmo id repetido (ou mesmo lote+nome sem id): fica o mais completo/recente.
    const itensCard = Array.isArray(card.itens) ? card.itens.length : 0;
    const itensPrev = Array.isArray(prev.itens) ? prev.itens.length : 0;
    const tCard = Date.parse(String(card.dataCriacaoISO || card.salvoEm || '')) || 0;
    const tPrev = Date.parse(String(prev.dataCriacaoISO || prev.salvoEm || '')) || 0;
    if (itensCard > itensPrev || (itensCard === itensPrev && tCard >= tPrev)) {
      porChave.set(chave, card);
    }
  }
  return Array.from(porChave.values());
}

export function chaveLotePersistencia(loteDigitado: string): string {
  const nums = numerosLoteDeTexto(loteDigitado);
  if (nums.length === 0) return normalizarChaveProduto(loteDigitado);
  return nums.join('-');
}

function nomeProdutoParaCadastroLotes(nomeCard: string): string {
  return String(nomeCard || '')
    .replace(/\bMDF\b/gi, ' ')
    .replace(/\bAGLOMERADO\b/gi, ' ')
    .replace(/\bAGLO\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function qtdProdutoCadastroLotes(
  nomeProduto: string,
  loteDigitado: string,
  lotes: ReadonlyArray<{ lote: string; produtoNome: string; qtd: number }>
): number {
  const alvo = normalizarNomeProdutoPalete(nomeProdutoParaCadastroLotes(nomeProduto));
  if (!alvo) return 0;
  let total = 0;
  for (const lp of lotes) {
    if (!loteCompartilhaNumero(lp.lote, loteDigitado)) continue;
    const nomeLp = normalizarNomeProdutoPalete(lp.produtoNome);
    if (nomeLp !== alvo) continue;
    total += Number(lp.qtd) || 0;
  }
  return total;
}

/** Nome de card/produto com mais de um item (unificado) — não usar soma flexível no multiplicador. */
export function nomeProdutoCadastroComposto(nomeProduto: string): boolean {
  const alvo = normalizarNomeProdutoPalete(nomeProdutoParaCadastroLotes(nomeProduto));
  if (!alvo) return false;
  return (
    /\s+E\s+/.test(alvo) ||
    /\s*\/\s*/.test(alvo) ||
    /\s*,\s*/.test(alvo) ||
    /\s+\+\s+/.test(alvo)
  );
}

/** Casamento flexível (nome do card unificado vs cadastro Lotes). Não soma vários produtos se o nome for composto. */
export function qtdProdutoCadastroLotesFlexivel(
  nomeProduto: string,
  loteDigitado: string,
  lotes: ReadonlyArray<{ lote: string; produtoNome: string; qtd: number }>
): number {
  const exato = qtdProdutoCadastroLotes(nomeProduto, loteDigitado, lotes);
  if (exato > 0) return exato;

  const alvo = normalizarNomeProdutoPalete(nomeProdutoParaCadastroLotes(nomeProduto));
  if (!alvo || nomeProdutoCadastroComposto(nomeProduto)) return 0;

  let total = 0;
  for (const lp of lotes) {
    if (!loteCompartilhaNumero(lp.lote, loteDigitado)) continue;
    const nomeLp = normalizarNomeProdutoPalete(lp.produtoNome);
    if (!nomeLp) continue;
    if (nomeLp === alvo || alvo.includes(nomeLp) || nomeLp.includes(alvo)) {
      total += Number(lp.qtd) || 0;
    }
  }
  return total;
}

/**
 * Em card unificado, descobre qual produto do cadastro a linha representa
 * (produtoOrigem explícito, ou tokens do nome da peça vs nomes dos produtos).
 * @deprecated Não usar para multiplicador — só cadastro de Lotes (ARUBA/CAYMAN etc.).
 */
export function resolverProdutoAlvoParaItem(
  produtoOrigemItem: string | undefined,
  descricaoItem: string,
  card: {
    nome?: string;
    produtosOrigemCompleto?: Array<{ nome?: string }>;
    produtosOrigem?: Array<{ nome?: string }>;
  }
): string {
  const explicit = String(produtoOrigemItem || '').trim();
  if (explicit) return explicit;

  const origens = [
    ...(Array.isArray(card.produtosOrigemCompleto) ? card.produtosOrigemCompleto : []),
    ...(Array.isArray(card.produtosOrigem) ? card.produtosOrigem : []),
  ]
    .map((o) => String(o?.nome || '').trim())
    .filter(Boolean);
  const unicos = [...new Set(origens)];

  if (unicos.length === 0) return String(card.nome || '').trim();
  if (unicos.length === 1) return unicos[0];

  const desc = normalizarChaveProduto(expandirAbreviacoesNomePeca(descricaoItem));
  let melhor = unicos[0];
  let melhorPts = -1;
  for (const o of unicos) {
    const oN = normalizarChaveProduto(o);
    let pts = 0;
    const tokens = oN.split(/\s+/).filter((t) => t.length >= 2);
    for (const tok of tokens) {
      if (desc.includes(tok)) pts += tok.length;
    }
    if (pts > melhorPts) {
      melhorPts = pts;
      melhor = o;
    }
  }
  return melhor;
}

/**
 * Vincula a linha do TC a um produto do cadastro de Lotes (ex.: ARUBA, CAYMAN).
 * Não usa nome da peça (MOLDURA INF/SUP) — só produtos que existem no cadastro do lote.
 */
export function vincularItemAoProdutoCadastroLote(
  nomeCandidato: string | undefined,
  loteDigitado: string,
  lotesCadastro: ReadonlyArray<{ lote: string; produtoNome: string; qtd: number }>
): { produtoNome: string; qtdLote: number } | null {
  const nome = String(nomeCandidato || '').trim();
  if (!nome || nomeProdutoCadastroComposto(nome)) return null;
  const q = qtdProdutoCadastroLotes(nome, loteDigitado, lotesCadastro);
  if (q > 0) return { produtoNome: nome, qtdLote: q };
  const qFlex = qtdProdutoCadastroLotesFlexivel(nome, loteDigitado, lotesCadastro);
  if (qFlex > 0) return { produtoNome: nome, qtdLote: qFlex };
  return null;
}

/**
 * Resolve multiplicador da peça: preferir cadastro de Lotes; senão qtd do card TC;
 * nunca descarta a peça (qtd mínima 1).
 */
function resolverMultiplicadorPeca(
  candidatos: string[],
  loteDigitado: string,
  lotesCadastro: ReadonlyArray<{ lote: string; produtoNome: string; qtd: number }>,
  qtdProdutoCard: number
): { produtoNome: string; qtdLote: number } {
  for (const c of candidatos) {
    const v = vincularItemAoProdutoCadastroLote(c, loteDigitado, lotesCadastro);
    if (v) return v;
  }
  const nomeFall = candidatos.map((c) => String(c || '').trim()).find(Boolean) || 'CARD TC';
  const qFall = Number(qtdProdutoCard) > 0 ? Number(qtdProdutoCard) : 1;
  return { produtoNome: nomeFall, qtdLote: qFall };
}

function chaveContribuicaoPeca(
  produtoCadastro: string,
  descricao: string,
  medida: string,
  material: string
): string {
  return `${normalizarChaveProduto(produtoCadastro)}|${chaveUnificacaoPeca(descricao, medida, material)}`;
}

function itemEstaSelecionado(selecionado: boolean | undefined): boolean {
  return selecionado !== false;
}

/** Interpreta datetime-local (sem fuso) ou ISO completo; retorna Date no fuso local. */
export function parseDateTimeLocal(valor: string): Date | null {
  const s = String(valor || '').trim();
  if (!s) return null;

  const semFuso = s.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (semFuso && !s.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(s)) {
    const d = new Date(
      Number(semFuso[1]),
      Number(semFuso[2]) - 1,
      Number(semFuso[3]),
      Number(semFuso[4]),
      Number(semFuso[5]),
      Number(semFuso[6] || 0),
      0
    );
    return isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

export function formatDateTimeLocal(data: Date): string {
  const ano = data.getFullYear();
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  const hora = String(data.getHours()).padStart(2, '0');
  const minuto = String(data.getMinutes()).padStart(2, '0');
  return `${ano}-${mes}-${dia}T${hora}:${minuto}`;
}

export function extrairHoraMinutoDateTimeLocal(valor: string): string {
  const d = parseDateTimeLocal(valor);
  if (!d) return '--:--';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function calcularInicioPlanejadoLote(
  loteDigitado: string,
  lotes: ReadonlyArray<{ lote: string; dataHoraInicioCorte?: string }>
): string {
  const nums = numerosLoteDeTexto(loteDigitado);
  const itens =
    nums.length > 0
      ? lotes.filter((item) => numerosLoteDeTexto(String(item.lote || '')).includes(nums[0]))
      : lotes.filter(
          (item) => normalizarChaveProduto(item.lote) === normalizarChaveProduto(loteDigitado)
        );

  const datas = itens
    .map((item) => item.dataHoraInicioCorte)
    .filter(Boolean)
    .map((raw) => parseDateTimeLocal(String(raw)))
    .filter((d): d is Date => d != null);

  if (datas.length === 0) return '';
  const menor = new Date(Math.min(...datas.map((d) => d.getTime())));
  return formatDateTimeLocal(menor);
}

/** Soma minutos ao horário de início (relógio de parede: 07:00 + 2h50 = 09:50). */
export function somarMinutosADataHoraLocal(inicio: string, minutos: number): string {
  const base = parseDateTimeLocal(inicio);
  if (!base) return '';
  const fim = new Date(base.getTime() + Math.round((Number(minutos) || 0) * 60 * 1000));
  return formatDateTimeLocal(fim);
}

export function extrairPecasBrutasDoLote(
  loteDigitado: string,
  produtosTc: ReadonlyArray<{
    id?: string;
    nome?: string;
    lote?: string;
    qtdProduto?: number;
    produtosOrigemCompleto?: Array<{ nome?: string; lote?: string; qtdProduto?: number }>;
    produtosOrigem?: Array<{ nome?: string; lote?: string; qtdProduto?: number }>;
    itens?: Array<{
      descricao?: string;
      qtd?: number;
      medida?: string;
      material?: string;
      metrosQuadrados?: number;
      selecionado?: boolean;
      produtoOrigem?: string;
    }>;
  }>,
  lotesCadastro: ReadonlyArray<{ lote: string; produtoNome: string; qtd: number }>
): PecaTempoCorteBruta[] {
  /**
   * Peça (desc+medida+material) → contribuições por card.
   * - Cards diferentes (ex.: produto + MDF): SOMA as qtds.
   * - Cópias do mesmo card (mesmo lote+nome): fica o maior (evita duplicata).
   */
  const contribPorPecaECard = new Map<
    string,
    Map<
      string,
      {
        descricao: string;
        medida: string;
        material: string;
        metrosQuadrados: number;
        produtoCadastro: string;
        qtd: number;
        cardNome: string;
      }
    >
  >();

  for (const card of listarCardsTempoCorteDoLote(
    produtosTc,
    loteDigitado,
    (c) => cardTempoCorteFoiExcluidoManual(c)
  )) {
    const nomeCard = String(card.nome || '').trim();
    const itens = Array.isArray(card.itens) ? card.itens : [];
    const cardId = String(card.id || nomeCard || 'card');
    const qtdProdutoCard = Number(card.qtdProduto) || 0;
    const numsLoteCard = numerosLoteDeTexto(String(card.lote || '')).join('-');
    const cardDedupKey = `${numsLoteCard}|${normalizarChaveProduto(nomeCard) || cardId}`;
    const nomesOrigem = [
      ...(Array.isArray(card.produtosOrigemCompleto) ? card.produtosOrigemCompleto : []),
      ...(Array.isArray(card.produtosOrigem) ? card.produtosOrigem : []),
    ]
      .map((o) => String(o?.nome || '').trim())
      .filter(Boolean);

    /** Soma qtd da tabela dentro do mesmo card (várias linhas iguais). */
    const somaPorPecaNoCard = new Map<
      string,
      {
        descricao: string;
        medida: string;
        material: string;
        metrosQuadrados: number;
        somaQtdTab: number;
        produtoCadastro: string;
        qtdLote: number;
      }
    >();

    for (const item of itens) {
      if (!itemEstaSelecionado(item.selecionado)) continue;
      const qtdBase = Number(item.qtd) || 0;
      if (qtdBase <= 0) continue;

      const descricao = String(item.descricao || '').trim();
      const medida = String(item.medida || '').trim();
      const material = String(item.material || '').trim();
      if (!descricao && !medida && !material) continue;

      const origemItem = String(item.produtoOrigem || '').trim();
      const candidatos = [origemItem, ...nomesOrigem, nomeCard].filter(Boolean);

      const vinculo = resolverMultiplicadorPeca(
        candidatos,
        loteDigitado,
        lotesCadastro,
        qtdProdutoCard
      );

      const kPeca = chaveUnificacaoPeca(descricao || '(sem descrição)', medida, material);
      const prev = somaPorPecaNoCard.get(kPeca);
      if (prev) {
        prev.somaQtdTab += qtdBase;
        prev.metrosQuadrados += Number(item.metrosQuadrados) || 0;
      } else {
        somaPorPecaNoCard.set(kPeca, {
          descricao: descricao || '(sem descrição)',
          medida,
          material,
          metrosQuadrados: Number(item.metrosQuadrados) || 0,
          somaQtdTab: qtdBase,
          produtoCadastro: vinculo.produtoNome,
          qtdLote: vinculo.qtdLote,
        });
      }
    }

    for (const [kPeca, row] of somaPorPecaNoCard.entries()) {
      const qLote = row.qtdLote > 0 ? row.qtdLote : 1;
      const qtdContrib = row.somaQtdTab * qLote;
      let porCard = contribPorPecaECard.get(kPeca);
      if (!porCard) {
        porCard = new Map();
        contribPorPecaECard.set(kPeca, porCard);
      }
      const existente = porCard.get(cardDedupKey);
      if (!existente || qtdContrib > existente.qtd) {
        porCard.set(cardDedupKey, {
          descricao: row.descricao,
          medida: row.medida,
          material: row.material,
          metrosQuadrados: row.metrosQuadrados,
          produtoCadastro: row.produtoCadastro,
          qtd: qtdContrib,
          cardNome: `${cardId}|${nomeCard}`,
        });
      }
    }
  }

  const brutas: PecaTempoCorteBruta[] = [];
  for (const porCard of contribPorPecaECard.values()) {
    const linhas = [...porCard.values()];
    if (linhas.length === 0) continue;
    const base = linhas[0];
    brutas.push({
      descricao: base.descricao,
      qtd: linhas.reduce((acc, l) => acc + l.qtd, 0),
      medida: base.medida,
      material: base.material,
      metrosQuadrados: linhas.reduce((acc, l) => acc + l.metrosQuadrados, 0),
      produtoOrigem: [...new Set(linhas.map((l) => l.produtoCadastro).filter(Boolean))].join(' + '),
      cardNome: linhas.map((l) => l.cardNome).join(' + '),
    });
  }
  return brutas;
}

export function unificarPecasLote(pecas: PecaTempoCorteBruta[]): LinhaTempoProducaoPeca[] {
  const grupos = new Map<
    string,
    {
      medida: string;
      material: string;
      nomes: Set<string>;
      qtd: number;
      metrosQuadrados: number;
      origens: Set<string>;
    }
  >();

  for (const p of pecas) {
    const medida = String(p.medida || '').trim();
    const material = String(p.material || '').trim();
    const chaveGrupo = chaveMedidaMaterial(medida, material);
    if (!chaveGrupo.replace(/\|/g, '').trim()) continue;

    const nome = descricaoExibicaoPeca(p.descricao);
    if (!nome) continue;

    const g = grupos.get(chaveGrupo) || {
      medida,
      material,
      nomes: new Set<string>(),
      qtd: 0,
      metrosQuadrados: 0,
      origens: new Set<string>(),
    };

    g.nomes.add(nome);
    g.qtd += p.qtd;
    g.metrosQuadrados += p.metrosQuadrados;
    const origem = String(p.cardNome || p.produtoOrigem || '').trim();
    if (origem) g.origens.add(origem);
    grupos.set(chaveGrupo, g);
  }

  return [...grupos.values()]
    .map((g) => {
      const nomes = [...g.nomes];
      const descricao = juntarNomesPecas(nomes);
      const chave = `${descricao}|${chaveMedidaMaterial(g.medida, g.material)}`;
      return {
        id: chave,
        descricaoExibicao: descricao,
        chaveUnificacao: chave,
        qtd: g.qtd,
        medida: g.medida,
        material: g.material,
        metrosQuadrados: g.metrosQuadrados,
        tempoLinhaMinutos: 0,
        origens: [...g.origens],
      };
    })
    .sort(compararLinhasTempoProducaoPeca);
}

export function totalMinutosLinhas(linhas: ReadonlyArray<{ tempoLinhaMinutos: number }>): number {
  return linhas.reduce((acc, l) => acc + (Number(l.tempoLinhaMinutos) || 0), 0);
}

function tempoSalvoPorMedidaMaterial(
  gerada: LinhaTempoProducaoPeca,
  mapSalvo: Map<string, LinhaTempoProducaoPeca>
): number | null {
  const partes = gerada.chaveUnificacao.split('|');
  if (partes.length < 3) return null;
  const medMat = partes.slice(1).join('|');

  let total = 0;
  let achou = false;
  for (const [chave, salvo] of mapSalvo.entries()) {
    if (chave === gerada.chaveUnificacao) continue;
    if (!chave.endsWith(`|${medMat}`)) continue;
    const t = Number(salvo.tempoLinhaMinutos) || 0;
    if (t <= 0) continue;
    total += t;
    achou = true;
  }
  return achou ? total : null;
}

export function mesclarLinhasPersistidas(
  geradas: LinhaTempoProducaoPeca[],
  salvas: LinhaTempoProducaoPeca[] | undefined
): LinhaTempoProducaoPeca[] {
  if (!salvas?.length) return geradas;
  const mapSalvo = new Map(salvas.map((l) => [l.chaveUnificacao, l]));
  return geradas.map((g) => {
    const s = mapSalvo.get(g.chaveUnificacao);
    if (s) {
      return {
        ...g,
        tempoLinhaMinutos: Number(s.tempoLinhaMinutos) || 0,
      };
    }
    const tempoPar = tempoSalvoPorMedidaMaterial(g, mapSalvo);
    if (tempoPar != null && tempoPar > 0) {
      return { ...g, tempoLinhaMinutos: tempoPar };
    }
    return g;
  });
}
